import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  TrendingUp, 
  RefreshCw, 
  Repeat2,
  Star,
  Pin,
  PinOff,
  Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BulkPostModal from '../BulkPostModal';

const PINNED_PRODUCTS_KEY = 'dealshaq_pinned_products';

export default function FrequentlyScanned() {
  const [frequentProducts, setFrequentProducts] = useState([]);
  const [pinnedCodes, setPinnedCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [showBulkPost, setShowBulkPost] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    // Load pinned products from localStorage
    const saved = localStorage.getItem(PINNED_PRODUCTS_KEY);
    if (saved) {
      try {
        setPinnedCodes(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse pinned products:', e);
      }
    }
    fetchFrequentProducts();
  }, []);

  const fetchFrequentProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/barcode/history/frequent?limit=5&days=30');
      if (response.data.success) {
        setFrequentProducts(response.data.products);
      }
    } catch (error) {
      console.error('Failed to fetch frequent products:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePin = (code) => {
    let newPinned;
    if (pinnedCodes.includes(code)) {
      newPinned = pinnedCodes.filter(c => c !== code);
    } else {
      newPinned = [...pinnedCodes, code].slice(0, 5); // Max 5 pinned
    }
    setPinnedCodes(newPinned);
    localStorage.setItem(PINNED_PRODUCTS_KEY, JSON.stringify(newPinned));
  };

  const toggleSelection = (code) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const getSelectedProducts = () => {
    return frequentProducts.filter(p => {
      const code = p.raw_code || p.barcode;
      return selectedCodes.has(code);
    });
  };

  const handleBulkPostSuccess = () => {
    setSelectedCodes(new Set());
    setShowBulkPost(false);
    fetchFrequentProducts();
  };

  const handlePostAgain = (product) => {
    // Navigate to scan page with product data for quick re-posting
    // Use raw_code from the API response
    const barcodeCode = product.raw_code || product.barcode;
    navigate('/retailer/scan', { 
      state: { 
        repostData: {
          raw_code: barcodeCode,
          format: product.format,
          product_name: product.product_name,
          category: product.category
        }
      } 
    });
  };

  // Sort products: pinned first, then by scan_count
  // Use raw_code from API response
  const sortedProducts = [...frequentProducts].sort((a, b) => {
    const aCode = a.raw_code || a.barcode;
    const bCode = b.raw_code || b.barcode;
    const aPinned = pinnedCodes.includes(aCode);
    const bPinned = pinnedCodes.includes(bCode);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    return b.scan_count - a.scan_count;
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-500" />
            {t('retailer.dashboard.frequently_scanned', 'Frequently Scanned')}
          </CardTitle>
          <div className="flex items-center gap-1">
            {selectedCodes.size > 0 && (
              <Button
                variant="default"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowBulkPost(true)}
                data-testid="bulk-post-frequent-btn"
              >
                <Package className="w-3 h-3 mr-1" />
                Post {selectedCodes.size}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={fetchFrequentProducts}
              disabled={loading}
              data-testid="frequent-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription className="text-xs">
          {t('retailer.dashboard.frequently_scanned_desc', 'Select items for bulk posting')}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        {loading ? (
          <div className="py-4 text-center text-gray-500 text-sm">
            {t('common.loading', 'Loading...')}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="py-6 text-center">
            <TrendingUp className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {t('retailer.dashboard.no_frequent_data', 'No frequent scan data yet')}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {t('retailer.dashboard.scan_to_see_trends', 'Scan products to see trends')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedProducts.slice(0, 5).map((product, index) => {
              const productCode = product.raw_code || product.barcode;
              const isPinned = pinnedCodes.includes(productCode);
              const isSelected = selectedCodes.has(productCode);
              return (
                <div 
                  key={productCode}
                  className={`flex items-center gap-2 p-2 rounded-lg transition-colors group ${
                    isSelected ? 'bg-blue-50 border border-blue-200' : 
                    isPinned ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`frequent-item-${index}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(productCode)}
                    className="h-4 w-4"
                    data-testid={`frequent-checkbox-${index}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isPinned && (
                        <Star className="w-3 h-3 text-orange-500 fill-orange-500" />
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {product.product_name || productCode}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-[10px] font-mono bg-gray-100 px-1 py-0.5 rounded">
                        {productCode}
                      </code>
                      <Badge variant="outline" className="text-[9px]">
                        {product.format}
                      </Badge>
                      <span className="text-[10px] text-gray-400">
                        {product.scan_count}x {t('retailer.dashboard.scanned', 'scanned')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-50 hover:opacity-100"
                      onClick={() => togglePin(productCode)}
                      data-testid={`pin-btn-${index}`}
                      title={isPinned ? t('retailer.dashboard.unpin', 'Unpin') : t('retailer.dashboard.pin', 'Pin')}
                    >
                      {isPinned ? (
                        <PinOff className="w-3.5 h-3.5 text-orange-500" />
                      ) : (
                        <Pin className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handlePostAgain(product)}
                      data-testid={`post-again-btn-${index}`}
                    >
                      <Repeat2 className="w-3 h-3 mr-1" />
                      {t('retailer.dashboard.post_again', 'Post')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      
      {/* Bulk Post Modal */}
      <BulkPostModal
        open={showBulkPost}
        onOpenChange={setShowBulkPost}
        selectedProducts={getSelectedProducts()}
        source="frequent_scans"
        onSuccess={handleBulkPostSuccess}
      />
    </Card>
  );
}
